import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Upload,
  FileText,
  AlertCircle,
  CheckCircle2,
  Clock,
  Eye,
  Pencil,
  Send,
  XCircle,
  ChevronRight,
  RefreshCw,
  Filter,
  Download,
  Merge,
  Split,
  UserPlus,
  History,
  BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransactionOperations = () => {
  const { getAuthHeaders } = useAuth();
  const [activeTab, setActiveTab] = useState('upload');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [batches, setBatches] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [draftLedger, setDraftLedger] = useState([]);
  const [publishedLedger, setPublishedLedger] = useState([]);
  const [consolidations, setConsolidations] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  
  // Upload states
  const [uploadFile, setUploadFile] = useState(null);
  const [dateRangeLabel, setDateRangeLabel] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  
  // Selection states
  const [selectedDraft, setSelectedDraft] = useState([]);
  const [selectedPublished, setSelectedPublished] = useState([]);
  const [userFilter, setUserFilter] = useState('all');
  
  // Modal states
  const [batchDetailModal, setBatchDetailModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [reviewActionModal, setReviewActionModal] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [statsRes, batchesRes, reviewRes, draftRes, publishedRes, consolidationsRes, auditRes] = await Promise.all([
        axios.get(`${API}/admin/mercury/stats`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/mercury/batches`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/mercury/review-queue`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/mercury/draft-ledger`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/mercury/published-ledger`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/mercury/consolidations`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/mercury/audit?limit=50`, { headers, withCredentials: true })
      ]);
      setStats(statsRes.data);
      setBatches(batchesRes.data);
      setReviewQueue(reviewRes.data);
      setDraftLedger(draftRes.data);
      setPublishedLedger(publishedRes.data);
      setConsolidations(consolidationsRes.data);
      setAuditLog(auditRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    const absAmount = Math.abs(amount || 0);
    const sign = amount < 0 ? '-' : '+';
    return `${sign}$${absAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Upload handler
  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (dateRangeLabel) {
        formData.append('date_range_label', dateRangeLabel);
      }
      
      const response = await axios.post(`${API}/admin/mercury/upload`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      
      setUploadResult(response.data);
      toast.success(`Import complete: ${response.data.imported_rows} transactions processed`);
      setUploadFile(null);
      setDateRangeLabel('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setLoading(false);
    }
  };

  // Review actions
  const handleReviewAction = async (itemId, action, targetUser = null) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/mercury/review/${itemId}/action`, {
        action,
        target_user: targetUser
      }, { headers, withCredentials: true });
      toast.success(`Action "${action}" completed`);
      setReviewActionModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  // Publish/Unpublish
  const handlePublish = async (ids) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/mercury/publish`, { transaction_ids: ids }, { headers, withCredentials: true });
      toast.success(`${ids.length} transaction(s) published`);
      setSelectedDraft([]);
      fetchData();
    } catch (error) {
      toast.error('Publish failed');
    }
  };

  const handleUnpublish = async (ids) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/mercury/unpublish`, { transaction_ids: ids }, { headers, withCredentials: true });
      toast.success(`${ids.length} transaction(s) unpublished`);
      setSelectedPublished([]);
      fetchData();
    } catch (error) {
      toast.error('Unpublish failed');
    }
  };

  // Edit derived transaction
  const handleEditSave = async () => {
    if (!editModal) return;
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/mercury/derived/${editModal.id}`, {
        display_description: editModal.display_description,
        display_amount: editModal.display_amount,
        display_date: editModal.display_date
      }, { headers, withCredentials: true });
      toast.success('Transaction updated');
      setEditModal(null);
      fetchData();
    } catch (error) {
      toast.error('Update failed');
    }
  };

  // Split consolidation
  const handleSplit = async (derivedId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/mercury/consolidations/${derivedId}/split`, {}, { headers, withCredentials: true });
      toast.success('Consolidation split into individual transactions');
      fetchData();
    } catch (error) {
      toast.error('Split failed');
    }
  };

  // Filter transactions by user
  const filterByUser = (transactions) => {
    if (userFilter === 'all') return transactions;
    return transactions.filter(t => t.mapped_user === userFilter);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'imported': { color: 'bg-blue-500/20 text-blue-400 border-blue-500', label: 'Imported' },
      'normalised': { color: 'bg-purple-500/20 text-purple-400 border-purple-500', label: 'Normalised' },
      'matched': { color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500', label: 'Matched' },
      'needs_review': { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500', label: 'Needs Review' },
      'ready_to_publish': { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500', label: 'Ready to Publish' },
      'published': { color: 'bg-green-500/20 text-green-400 border-green-500', label: 'Published' },
      'rejected': { color: 'bg-red-500/20 text-red-400 border-red-500', label: 'Rejected' },
      'ignored': { color: 'bg-gray-500/20 text-gray-400 border-gray-500', label: 'Ignored' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-500/20 text-gray-400', label: status };
    return <Badge variant="outline" className={`${config.color} text-xs`}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="transaction-operations">
      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-semibold">{stats.total_batches}</p>
              <p className="text-xs text-muted-foreground">Import Batches</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-semibold">{stats.total_raw_transactions}</p>
              <p className="text-xs text-muted-foreground">Raw Rows</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-semibold text-yellow-400">{stats.pending_review}</p>
              <p className="text-xs text-muted-foreground">Pending Review</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-semibold text-emerald-400">{stats.ready_to_publish}</p>
              <p className="text-xs text-muted-foreground">Ready to Publish</p>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-heading font-semibold text-green-400">{stats.published}</p>
              <p className="text-xs text-muted-foreground">Published</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary">
          <TabsTrigger value="upload" className="data-[state=active]:bg-accent">
            <Upload className="w-4 h-4 mr-2" /> Upload
          </TabsTrigger>
          <TabsTrigger value="batches" className="data-[state=active]:bg-accent">
            <FileText className="w-4 h-4 mr-2" /> Import Batches
          </TabsTrigger>
          <TabsTrigger value="review" className="data-[state=active]:bg-accent">
            <AlertCircle className="w-4 h-4 mr-2" /> Review Queue
            {stats?.pending_review > 0 && (
              <Badge className="ml-2 bg-yellow-500/20 text-yellow-400">{stats.pending_review}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="draft" className="data-[state=active]:bg-accent">
            <Clock className="w-4 h-4 mr-2" /> Draft Ledger
          </TabsTrigger>
          <TabsTrigger value="published" className="data-[state=active]:bg-accent">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Published
          </TabsTrigger>
          <TabsTrigger value="consolidations" className="data-[state=active]:bg-accent">
            <Merge className="w-4 h-4 mr-2" /> Consolidations
          </TabsTrigger>
          <TabsTrigger value="audit" className="data-[state=active]:bg-accent">
            <History className="w-4 h-4 mr-2" /> Audit Log
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Upload Mercury CSV</CardTitle>
                <CardDescription>Import transaction data from Mercury export</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>CSV File</Label>
                  <Input 
                    type="file"
                    accept=".csv"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="bg-background cursor-pointer"
                    data-testid="mercury-file-input"
                  />
                </div>
                <div>
                  <Label>Date Range Label (Optional)</Label>
                  <Input 
                    placeholder="e.g., 01 | 01 May 2026 - 05 May 2026"
                    value={dateRangeLabel}
                    onChange={(e) => setDateRangeLabel(e.target.value)}
                    className="bg-background"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Display label for this import batch</p>
                </div>
                <Button 
                  onClick={handleUpload}
                  className="w-full bg-white text-[#0A0F1C]"
                  disabled={!uploadFile || loading}
                  data-testid="mercury-upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> 
                  {loading ? 'Processing...' : 'Upload & Process'}
                </Button>
              </CardContent>
            </Card>

            {uploadResult && (
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-emerald-400">Import Complete</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total Rows</span>
                      <span>{uploadResult.total_rows}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Imported</span>
                      <span className="text-emerald-400">{uploadResult.imported_rows}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Duplicates Blocked</span>
                      <span className="text-blue-400">{uploadResult.duplicate_rows}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Near Duplicates</span>
                      <span className="text-yellow-400">{uploadResult.near_duplicate_rows}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Needs Review</span>
                      <span className="text-yellow-400">{uploadResult.review_rows}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Fee Consolidations</span>
                      <span className="text-cyan-400">{uploadResult.consolidation_pairs}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Ready to Publish</span>
                      <span className="text-emerald-400">{uploadResult.ready_to_publish_count}</span>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Detected Accounts:</p>
                    <div className="flex gap-2">
                      {uploadResult.detected_nicknames?.map(n => (
                        <Badge key={n} variant="outline">{n}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Mapped Users:</p>
                    <div className="flex gap-2">
                      {uploadResult.mapped_users?.map(u => (
                        <Badge key={u} variant="outline" className="bg-accent/20">{u}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Mercury CSV Format Reference</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-background rounded-lg p-4 font-mono text-xs overflow-x-auto">
                <p className="text-muted-foreground mb-2">Expected columns:</p>
                <code className="text-emerald-400">Date (UTC), Description, Amount, Status, Source Account, Bank Description, Reference, Note, Last Four Digits, Name On Card, Merchant Type, Category, Timestamp, Original Currency, Cardholder Email, Tracking ID</code>
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                <p><strong>Account Routing:</strong> Source Account field determines user routing</p>
                <p className="mt-1">01 → cre01 | 02 → cre02 | 03 → cre03</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Import Batches Tab */}
        <TabsContent value="batches">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Import Batches</CardTitle>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase">File</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Label</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Account</TableHead>
                    <TableHead className="text-xs font-bold uppercase">User</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-center">Total</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-center">Imported</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-center">Review</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batches.map((batch) => (
                    <TableRow key={batch.id} className="border-border">
                      <TableCell className="text-sm">{formatDateTime(batch.uploaded_at)}</TableCell>
                      <TableCell className="text-sm font-mono">{batch.filename}</TableCell>
                      <TableCell className="text-sm">{batch.date_range_label || '-'}</TableCell>
                      <TableCell>
                        {batch.detected_account_nicknames?.map(n => (
                          <Badge key={n} variant="outline" className="mr-1">{n}</Badge>
                        ))}
                      </TableCell>
                      <TableCell>
                        {batch.mapped_users?.map(u => (
                          <Badge key={u} variant="outline" className="bg-accent/20 mr-1">{u}</Badge>
                        ))}
                      </TableCell>
                      <TableCell className="text-center">{batch.total_rows}</TableCell>
                      <TableCell className="text-center text-emerald-400">{batch.imported_rows}</TableCell>
                      <TableCell className="text-center text-yellow-400">{batch.review_rows}</TableCell>
                      <TableCell>{getStatusBadge(batch.status)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setBatchDetailModal(batch)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {batches.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                        No import batches yet
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Review Queue Tab */}
        <TabsContent value="review">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Review Queue</CardTitle>
              <CardDescription>Items requiring admin attention before publishing</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Description</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Amount</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Account</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Reason</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reviewQueue.map((item) => (
                    <TableRow key={item.id} className="border-border">
                      <TableCell className="text-sm">{formatDate(item.transaction?.normalised_date)}</TableCell>
                      <TableCell className="text-sm font-medium">{item.transaction?.normalised_description || '-'}</TableCell>
                      <TableCell className={`text-sm font-mono text-right ${item.transaction?.signed_amount < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {formatCurrency(item.transaction?.signed_amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.transaction?.account_nickname || 'Unknown'}</Badge>
                      </TableCell>
                      <TableCell>
                        {item.reasons?.map(r => (
                          <Badge key={r} variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-500 mr-1 text-xs">
                            {r.replace(/_/g, ' ')}
                          </Badge>
                        ))}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleReviewAction(item.id, 'approve')} title="Approve">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setReviewActionModal(item)} title="Assign User">
                            <UserPlus className="w-4 h-4 text-blue-400" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleReviewAction(item.id, 'ignore')} title="Ignore">
                            <XCircle className="w-4 h-4 text-gray-400" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleReviewAction(item.id, 'reject')} title="Reject">
                            <XCircle className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {reviewQueue.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                        No items pending review
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Draft Ledger Tab */}
        <TabsContent value="draft">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">Draft Ledger</CardTitle>
                <CardDescription>Unpublished transactions ready for review</CardDescription>
              </div>
              <div className="flex gap-2">
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="w-32 bg-background">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="cre01">CRE-01</SelectItem>
                    <SelectItem value="cre02">CRE-02</SelectItem>
                    <SelectItem value="cre03">CRE-03</SelectItem>
                  </SelectContent>
                </Select>
                {selectedDraft.length > 0 && (
                  <Button 
                    className="bg-success hover:bg-success/90"
                    onClick={() => handlePublish(selectedDraft)}
                  >
                    <Send className="w-4 h-4 mr-2" /> Publish Selected ({selectedDraft.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedDraft.length === filterByUser(draftLedger).length && draftLedger.length > 0}
                        onCheckedChange={(checked) => {
                          setSelectedDraft(checked ? filterByUser(draftLedger).map(t => t.id) : []);
                        }}
                      />
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase">User</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Description</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Amount</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Type</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filterByUser(draftLedger).map((tx) => (
                    <TableRow key={tx.id} className="border-border">
                      <TableCell>
                        <Checkbox 
                          checked={selectedDraft.includes(tx.id)}
                          onCheckedChange={(checked) => {
                            setSelectedDraft(checked 
                              ? [...selectedDraft, tx.id]
                              : selectedDraft.filter(id => id !== tx.id)
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(tx.display_date)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-accent/20">{tx.mapped_user}</Badge>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{tx.display_description}</TableCell>
                      <TableCell className={`text-sm font-mono text-right ${tx.display_amount < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {formatCurrency(tx.display_amount)}
                      </TableCell>
                      <TableCell>
                        {tx.is_derived && tx.enrichment_type === 'intl_fee_combination' && (
                          <Badge variant="outline" className="bg-cyan-500/20 text-cyan-400 text-xs">Combined</Badge>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(tx.status)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setEditModal({...tx})}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handlePublish([tx.id])}>
                            <Send className="w-4 h-4 text-emerald-400" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filterByUser(draftLedger).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        No draft transactions
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Published Ledger Tab */}
        <TabsContent value="published">
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading text-lg">Published Ledger</CardTitle>
                <CardDescription>Transactions visible to clients</CardDescription>
              </div>
              <div className="flex gap-2">
                <Select value={userFilter} onValueChange={setUserFilter}>
                  <SelectTrigger className="w-32 bg-background">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="cre01">CRE-01</SelectItem>
                    <SelectItem value="cre02">CRE-02</SelectItem>
                    <SelectItem value="cre03">CRE-03</SelectItem>
                  </SelectContent>
                </Select>
                {selectedPublished.length > 0 && (
                  <Button 
                    variant="outline"
                    onClick={() => handleUnpublish(selectedPublished)}
                  >
                    Unpublish Selected ({selectedPublished.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedPublished.length === filterByUser(publishedLedger).length && publishedLedger.length > 0}
                        onCheckedChange={(checked) => {
                          setSelectedPublished(checked ? filterByUser(publishedLedger).map(t => t.id) : []);
                        }}
                      />
                    </TableHead>
                    <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase">User</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Description</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Amount</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Published</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filterByUser(publishedLedger).map((tx) => (
                    <TableRow key={tx.id} className="border-border">
                      <TableCell>
                        <Checkbox 
                          checked={selectedPublished.includes(tx.id)}
                          onCheckedChange={(checked) => {
                            setSelectedPublished(checked 
                              ? [...selectedPublished, tx.id]
                              : selectedPublished.filter(id => id !== tx.id)
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-sm">{formatDate(tx.display_date)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-accent/20">{tx.mapped_user}</Badge>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{tx.display_description}</TableCell>
                      <TableCell className={`text-sm font-mono text-right ${tx.display_amount < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {formatCurrency(tx.display_amount)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDateTime(tx.published_at)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleUnpublish([tx.id])}>
                          <XCircle className="w-4 h-4 text-red-400" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filterByUser(publishedLedger).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No published transactions
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consolidations Tab */}
        <TabsContent value="consolidations">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Fee Consolidations</CardTitle>
              <CardDescription>International transaction fees combined with merchant purchases</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs font-bold uppercase">Date</TableHead>
                    <TableHead className="text-xs font-bold uppercase">User</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Combined Description</TableHead>
                    <TableHead className="text-xs font-bold uppercase text-right">Combined Amount</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Confidence</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {consolidations.map((tx) => (
                    <TableRow key={tx.id} className="border-border">
                      <TableCell className="text-sm">{formatDate(tx.display_date)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-accent/20">{tx.mapped_user}</Badge>
                      </TableCell>
                      <TableCell className="text-sm font-medium">{tx.display_description}</TableCell>
                      <TableCell className="text-sm font-mono text-right text-red-400">
                        {formatCurrency(tx.display_amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={tx.consolidation_confidence >= 70 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'}>
                          {tx.consolidation_confidence}%
                        </Badge>
                      </TableCell>
                      <TableCell>{getStatusBadge(tx.status)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => handleSplit(tx.id)} title="Split">
                          <Split className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {consolidations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No fee consolidations
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Log Tab */}
        <TabsContent value="audit">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Audit Log</CardTitle>
              <CardDescription>Complete audit trail of transaction operations</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs font-bold uppercase">Timestamp</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Action</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Actor</TableHead>
                    <TableHead className="text-xs font-bold uppercase">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLog.map((event) => (
                    <TableRow key={event.id} className="border-border">
                      <TableCell className="text-sm">{formatDateTime(event.timestamp)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{event.action.replace(/_/g, ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{event.actor}</TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {JSON.stringify(event.details).slice(0, 100)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {auditLog.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No audit events
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Modal */}
      <Dialog open={!!editModal} onOpenChange={() => setEditModal(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Derived Transaction</DialogTitle>
          </DialogHeader>
          {editModal && (
            <div className="space-y-4">
              <div>
                <Label>Display Date</Label>
                <Input 
                  type="date"
                  value={editModal.display_date || ''}
                  onChange={(e) => setEditModal({...editModal, display_date: e.target.value})}
                  className="bg-background"
                />
              </div>
              <div>
                <Label>Display Description</Label>
                <Input 
                  value={editModal.display_description || ''}
                  onChange={(e) => setEditModal({...editModal, display_description: e.target.value})}
                  className="bg-background"
                />
              </div>
              <div>
                <Label>Display Amount</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={editModal.display_amount || 0}
                  onChange={(e) => setEditModal({...editModal, display_amount: parseFloat(e.target.value)})}
                  className="bg-background"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleEditSave} className="bg-white text-[#0A0F1C]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Action Modal (Assign User) */}
      <Dialog open={!!reviewActionModal} onOpenChange={() => setReviewActionModal(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Assign User</DialogTitle>
          </DialogHeader>
          {reviewActionModal && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Assign this transaction to a user for publishing.
              </p>
              <div className="flex gap-2">
                {['cre01', 'cre02', 'cre03'].map(user => (
                  <Button 
                    key={user}
                    variant="outline"
                    onClick={() => handleReviewAction(reviewActionModal.id, 'assign_user', user)}
                    className="flex-1"
                  >
                    {user.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionOperations;
